'use client';

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/form/Checkbox';
import { ScLink } from '@/components/form/groups/styled';
import { PrivacyPolicyModalContainer } from '@/components/modals/PrivacyPolicyModal';
import { TermsAndConditionsModalContainer } from '@/components/modals/TermsAndConditionsModal';
import { MIN_AGE } from '@/helpers/conf';
import { checkboxRequired } from '@/helpers/validators';
import { useAffiliateRegConfig } from '@/modules/promo';
import { useRegModalTheme } from '@/modules/registration/hooks/useRegModalTheme';
import {
  PersistentFormName,
  usePersistentFormsSlice,
} from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import { ScPromoCheckboxesContainer } from '../styled';

export interface RegistrationFormCheckboxListProps {
  formId?: string;
  showPromosCheckboxes: boolean;
  openInnerModalsInPortal?: boolean;
  isOneTermsByDefault?: boolean;
}

export const RegistrationFormCheckboxList = ({
  formId,
  showPromosCheckboxes,
  isOneTermsByDefault,
  openInnerModalsInPortal,
}: RegistrationFormCheckboxListProps) => {
  const { t } = useTranslation('common');
  const { newTerms, oneTerms, autoCheck } = useAffiliateRegConfig();
  const resolvedOneTerms = oneTerms || !!isOneTermsByDefault;
  const { getPersistentFormData } = usePersistentFormsSlice();
  const persisted = getPersistentFormData(PersistentFormName.PreRegistration);

  const { styleScheme, checkbox } = useRegModalTheme();

  const [isOpenPrivacy, setIsOpenPrivacy] = useState(false);
  const [isOpenTerms, setIsOpenTerms] = useState(false);

  const toggleTerms = (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e?.preventDefault();
    setIsOpenTerms((s) => !s);
  };

  const togglePrivacy = (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e?.preventDefault();
    setIsOpenPrivacy((s) => !s);
  };

  const age = MIN_AGE;

  return (
    <>
      <Checkbox
        checked={persisted?.termsAcceptanceAgeAcceptance || autoCheck}
        formId={formId}
        label={
          <Trans
            components={{
              1: <ScLink onClick={toggleTerms} styleScheme={styleScheme} />,
              2: <ScLink onClick={togglePrivacy} styleScheme={styleScheme} />,
            }}
            i18nKey={
              checkbox.termsLinkText ||
              'auth.registration.form.acceptTermsConditions'
            }
            ns="common"
            values={{ age }}
          />
        }
        name="termsAcceptanceAgeAcceptance"
        styleScheme={styleScheme}
        testId="registration-terms-checkbox"
        validate={checkboxRequired}
      />
      {!resolvedOneTerms && (
        <Checkbox
          checked={showPromosCheckboxes || (autoCheck && newTerms)}
          formId={formId}
          label={t(
            newTerms
              ? 'common:auth.registration.form.offersCheckboxes.stayTuned'
              : 'common:auth.registration.form.offersCheckboxes.refuseOffers'
          )}
          name="receivePromosMain"
          styleScheme={styleScheme}
          testId="registration-promo-main-checkbox"
          tooltip={t('common:auth.registration.form.offersCheckboxes.tooltip')}
        />
      )}
      {!(newTerms || resolvedOneTerms) && (
        <ScPromoCheckboxesContainer isOpen={showPromosCheckboxes}>
          <Checkbox
            checked={!!persisted.receivePromos}
            formId={formId}
            label={t(
              'common:auth.registration.form.offersCheckboxes.refuseEmailOffers'
            )}
            name="receivePromos"
            styleScheme={styleScheme}
            testId="registration-promo-email-checkbox"
          />
          <Checkbox
            checked={!!persisted.receiveSmsPromos}
            formId={formId}
            label={t(
              'common:auth.registration.form.offersCheckboxes.refuseSmsOffers'
            )}
            name="receiveSmsPromos"
            styleScheme={styleScheme}
            testId="registration-promo-sms-checkbox"
          />
          <Checkbox
            checked={!!persisted.receivePromosViaPhoneCalls}
            formId={formId}
            label={t(
              'common:auth.registration.form.offersCheckboxes.refusePhoneOffers'
            )}
            name="receivePromosViaPhoneCalls"
            styleScheme={styleScheme}
            testId="registration-promo-phone-checkbox"
          />
        </ScPromoCheckboxesContainer>
      )}
      {isOpenPrivacy && (
        <PrivacyPolicyModalContainer
          isPortal={openInnerModalsInPortal}
          onClose={togglePrivacy}
        />
      )}
      {isOpenTerms && (
        <TermsAndConditionsModalContainer
          isPortal={openInnerModalsInPortal}
          onClose={toggleTerms}
        />
      )}
    </>
  );
};
